import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

// UI-only client state - never GraphQL entities (those live in
// Apollo's normalized cache). Persisted to localStorage so locale +
// sidebar preferences survive a page reload.

export type Locale = "en" | "de";

interface UiState {
  locale: Locale;
  sidebarCollapsed: boolean;

  setLocale: (locale: Locale) => void;
  toggleSidebar: () => void;
}

const noopStorage: Storage = {
  length: 0,
  clear: () => undefined,
  getItem: () => null,
  key: () => null,
  removeItem: () => undefined,
  setItem: () => undefined,
};

// Defensive resolver - verifies the platform actually exposes a
// working Storage interface before returning it. Tests run in jsdom
// where some setups deliver a partial localStorage shim; treating
// that as "no storage" avoids a hard crash at first state change.
function resolveStorage(): Storage {
  try {
    if (typeof window === "undefined") return noopStorage;
    const candidate = window.localStorage;
    if (!candidate || typeof candidate.setItem !== "function") {
      return noopStorage;
    }
    return candidate;
  } catch {
    // Some browsers throw on `window.localStorage` access in
    // private-browsing mode. Fall through to noop.
    return noopStorage;
  }
}

function detectInitialLocale(): Locale {
  if (typeof navigator === "undefined") return "en";
  const browserLang = navigator.language?.slice(0, 2).toLowerCase();
  return browserLang === "de" ? "de" : "en";
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      locale: detectInitialLocale(),
      sidebarCollapsed: false,

      setLocale: (locale) => set({ locale }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
    }),
    {
      name: "spark-ui",
      storage: createJSONStorage(resolveStorage),
    },
  ),
);
